import {
  AttackAttribute,
  DangerousAttackAttribute,
  DetailType,
  ErrorAttribute,
  FreeKickAttribute,
  InPossessionAttribute,
  InterceptionAttribute,
  MissedPenaltyAttribute,
  PenaltyAttribute,
  PenaltySavedByGoalkeeperAttribute,
  RedCardAttribute,
  ShotBlockedAttribute,
  ShotOffTargetAttribute,
  ShotOnTargetAttribute,
  SoccerAttributeId,
  SoccerFoulAttribute,
  SoccerGoalAttribute,
  SoccerIncidentId,
  VideoAssistantRefereeAttribute,
  YellowCardAttribute,
} from '@collector/sportsapi-client-legacy'
import {
  SportAttributesConfiguration,
  SportDetailsConfiguration,
} from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'

import GoalDetailsModal from './Modals/GoalDetailsModal/GoalDetailsModal.vue'
import { getIncidentDetailsFillState as goalIncidentValidator } from './Modals/GoalDetailsModal/utils'
import ShotBlockedDetailsModal from './Modals/ShotBlockedDetailsModal/ShotBlockedDetailsModal.vue'
import { getIncidentDetailsFillState as shotBlockedIncidentValidator } from './Modals/ShotBlockedDetailsModal/utils'
import ShotOffTargetDetailsModal from './Modals/ShotOffTargetDetailsModal/ShotOffTargetDetailsModal.vue'
import { getIncidentDetailsFillState as shotOffTargetIncidentValidator } from './Modals/ShotOffTargetDetailsModal/utils'
import ShotOnTargetDetailsModal from './Modals/ShotOnTargetDetailsModal/ShotOnTargetDetailsModal.vue'
import { getIncidentDetailsFillState as shotOnTargetIncidentValidator } from './Modals/ShotOnTargetDetailsModal/utils'

export enum SoccerGroupName {
  Distance = 'Distance',
  PartOfBody = 'Part of body',
  Placement = 'Placement',
  ShotWoodwork = 'Shot woodwork',
  Goalkeeper = 'Goalkeeper',
  GoalType = 'Goal type',
  ShotType = 'Shot type',
  ErrorType = 'Error type',
  InterceptionType = 'Interception type',
}

export const SoccerDetailsConfiguration: SportDetailsConfiguration<
  SoccerIncidentId,
  SoccerAttributeId
> = {
  [SoccerIncidentId.Goal]: {
    component: GoalDetailsModal,
    getIncidentDetailsFillState: goalIncidentValidator,
  },
  [SoccerIncidentId.ShotOnTarget]: {
    component: ShotOnTargetDetailsModal,
    getIncidentDetailsFillState: shotOnTargetIncidentValidator,
  },
  [SoccerIncidentId.ShotOffTarget]: {
    component: ShotOffTargetDetailsModal,
    getIncidentDetailsFillState: shotOffTargetIncidentValidator,
  },
  [SoccerIncidentId.ShotBlocked]: {
    component: ShotBlockedDetailsModal,
    getIncidentDetailsFillState: shotBlockedIncidentValidator,
  },
  [SoccerIncidentId.Error]: [
    {
      name: SoccerGroupName.ErrorType,
      attributes: [
        ErrorAttribute.LeadingToAttempt,
        ErrorAttribute.LeadingToGoal,
      ],
      cols: 2,
      important: true,
      type: DetailType.Button,
    },
  ],
  [SoccerIncidentId.Interception]: [
    {
      name: SoccerGroupName.InterceptionType,
      // NOTE: Modal with single optional attribute is not the best UX,
      // but as this should be rare, good enough for now
      attributes: [InterceptionAttribute.LastLine],
      cols: 1,
      important: false,
      type: DetailType.Button,
    },
  ],
  [SoccerIncidentId.MissedPenalty]: [
    {
      name: SoccerGroupName.PartOfBody,
      attributes: [
        MissedPenaltyAttribute.LeftFoot,
        MissedPenaltyAttribute.RightFoot,
      ],
      cols: 2,
      important: true,
      type: DetailType.Button,
    },
    {
      name: SoccerGroupName.ShotWoodwork,
      attributes: [MissedPenaltyAttribute.ShotWoodwork],
      cols: 1,
      important: false,
      type: DetailType.Button,
    },
  ],
  [SoccerIncidentId.PenaltySavedByGoalkeeper]: [
    {
      name: SoccerGroupName.PartOfBody,
      attributes: [
        PenaltySavedByGoalkeeperAttribute.LeftFoot,
        PenaltySavedByGoalkeeperAttribute.RightFoot,
      ],
      cols: 2,
      important: true,
      type: DetailType.Button,
    },
    {
      name: SoccerGroupName.Placement,
      attributes: [
        PenaltySavedByGoalkeeperAttribute.UpperLeft,
        PenaltySavedByGoalkeeperAttribute.UpperMiddle,
        PenaltySavedByGoalkeeperAttribute.UpperRight,
        PenaltySavedByGoalkeeperAttribute.BottomLeft,
        PenaltySavedByGoalkeeperAttribute.BottomMiddle,
        PenaltySavedByGoalkeeperAttribute.BottomRight,
      ],
      cols: 3,
      important: true,
      type: DetailType.Button,
    },
    {
      name: SoccerGroupName.Goalkeeper,
      attributes: [],
      important: true,
      type: DetailType.Button,
      fulfilledWhen: (_, probableIncident) => {
        return !!probableIncident?.additional_info?.goalkeeper_id
      },
    },
  ],
}

export const SoccerAttributesConfiguration: SportAttributesConfiguration<
  SoccerIncidentId,
  SoccerAttributeId
> = {
  [SoccerIncidentId.Goal]: [
    SoccerGoalAttribute.OwnGoal,
    SoccerGoalAttribute.PenaltyGoal,
    SoccerGoalAttribute.NoAssist,
  ],
  [SoccerIncidentId.RedCard]: [
    RedCardAttribute.SecondYellowCard,
    RedCardAttribute.OffField,
  ],
  [SoccerIncidentId.YellowCard]: [YellowCardAttribute.OffField],
  [SoccerIncidentId.ShotOffTarget]: [
    ShotOffTargetAttribute.ShotWoodwork,
    ShotOffTargetAttribute.Post,
    ShotOffTargetAttribute.Crossbar,
  ],
  [SoccerIncidentId.MissedPenalty]: [MissedPenaltyAttribute.ShotWoodwork],
  [SoccerIncidentId.Penalty]: [PenaltyAttribute.Retaken],
  [SoccerIncidentId.FreeKick]: [FreeKickAttribute.DangerousFreeKick],
  [SoccerIncidentId.VideoAssistantReferee]: [
    VideoAssistantRefereeAttribute.PossibleGoal,
    VideoAssistantRefereeAttribute.PossiblePenalty,
    VideoAssistantRefereeAttribute.PossibleRedCard,
    VideoAssistantRefereeAttribute.PossibleYellowCard,
  ],
  [SoccerIncidentId.ShotOnTarget]: [ShotOnTargetAttribute.GoallineClearance],
  [SoccerIncidentId.Foul]: [SoccerFoulAttribute.CausesPenalty],
  [SoccerIncidentId.Attack]: [AttackAttribute.KeyPass],
  [SoccerIncidentId.DangerousAttack]: [DangerousAttackAttribute.KeyPass],
  [SoccerIncidentId.InPossession]: [InPossessionAttribute.KeyPass],
  [SoccerIncidentId.ShotBlocked]: [ShotBlockedAttribute.BlockedByTeammate],
  [SoccerIncidentId.Error]: [
    ErrorAttribute.LeadingToAttempt,
    ErrorAttribute.LeadingToGoal,
  ],
  [SoccerIncidentId.Interception]: [InterceptionAttribute.LastLine],
}
